import {AnimationGlobalEnum} from './animation-global.enum';

//#region ENUM AnimationFillModeEnum

/**
 * ### AnimationFillModeEnum
 * Enum representing possible values for the CSS `animation-fill-mode` property.
 *
 * This enum is accompanied by:
 * - `AnimationFillModeEnumHumanReadable` — human-friendly labels.
 * - `AnimationFillModeEnumType` — a union type of all enum keys.
 *
 * @enum AnimationFillModeEnum
 * @property BACKWARDS - The animation will apply styles defined in the first keyframe before it begins.
 * @property BOTH - The animation will follow the rules for both forwards and backwards.
 * @property FORWARDS - The animation will retain the style values set by the last keyframe.
 * @property NONE - The animation will not apply any styles outside its active duration.
 */
export enum AnimationFillModeEnum {
  /** The animation will apply styles defined in the first keyframe before it begins. */
  BACKWARDS = 'backwards',

  /** The animation will follow the rules for both forwards and backwards. */
  BOTH = 'both',

  /** The animation will retain the style values set by the last keyframe. */
  FORWARDS = 'forwards',

  /** The animation will not apply any styles outside its active duration. */
  NONE = 'none',
}

//#endregion

//#region TYPE AnimationFillModeEnumType
/**
 * ### AnimationFillModeEnumType
 * Union type of all keys in `AnimationFillModeEnum`.
 *
 * @type AnimationFillModeEnumType
 * @example
 * // Possible values: 'BACKWARDS' | 'BOTH' | 'FORWARDS' | 'NONE'
 */
export type AnimationFillModeEnumType = keyof typeof AnimationFillModeEnum;
//#endregion

//#region TYPE AnimationFillModeType
/**
 * ### AnimationFillModeType
 * Union type of all keys from `AnimationFillModeEnum` and `AnimationGlobalEnum`.
 *
 * This type is useful when a value can be either a fill-mode-specific or global animation keyword.
 *
 * @type AnimationFillModeType
 * @example
 * // Possible values:
 * // 'BACKWARDS' | 'BOTH' | 'FORWARDS' | 'NONE'
 * // | 'INHERIT' | 'INITIAL' | 'REVERT' | 'REVERT_LAYER' | 'UNSET'
 */
export type AnimationFillModeType =
  | keyof typeof AnimationFillModeEnum
  | keyof typeof AnimationGlobalEnum;
//#endregion
